/**
 * Create / Edit Custom Function — AI-Assisted Function Developer
 *
 * R2 P1-3 (PR #470 audit fix): repointed the CTA semantics. Tools and
 * Functions are distinct entity kinds post the U-series unification
 * (PR-U4 functions, PR-U5 tools). This route mounts the shared
 * ToolDeveloperPage shell scoped as a Function authoring surface —
 * helper chat is `function_helper` (scope='tenant'), title pill says
 * "Argus Function Developer" (not "Argus Tool Developer"), and the
 * save callback persists into `tenant_functions`.
 *
 * The underlying ToolDeveloperPage component is shared with the Tools
 * authoring flow on purpose — the editor + chat shell is identical;
 * only the helper kind, header strings, and save callback differ
 * (canonical-shared-component-with-scope-prop pattern from memory
 * `feedback_shared_component_scope_prop.md`).
 *
 * Search params:
 *   ?edit=functionId — loads existing function for AI-assisted editing
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ToolDeveloperPage } from '@/components/tool-developer';
import { useCreateTenantFunction, useTenantFunctions } from '@/lib/use-connections-module';
import { runtimeToEditorLanguage } from './-new-function.helpers';
import { ConnectionsRoleGate } from './-connections-role-gate';

export const Route = createFileRoute('/connections/new-function')({
  validateSearch: (search: Record<string, unknown>) => ({
    edit: (search.edit as string) || '',
  }),
  component: NewFunctionRoute,
});

// Tiny wrapper — pulls the search param off this route's Route object,
// then passes the resolved editId to the page component as a prop.
// Lets the round-4 #228 alias at /connections/functions/new reuse the
// same NewFunctionPage with its own search-param decoding.
function NewFunctionRoute() {
  const { edit } = Route.useSearch();
  return <NewFunctionPage editId={edit || undefined} />;
}

// Exported so the nested-IA alias route at /connections/functions/new
// (added round 4 / #228) can render the same component.
export function NewFunctionPage({ editId }: { editId?: string } = {}) {
  const navigate = useNavigate();
  const createFunction = useCreateTenantFunction();

  // If editing, fetch the existing function from real tenant_functions.
  const { data: realFns = [] } = useTenantFunctions();
  const existingFn = editId ? realFns.find(f => f.id === editId) : null;
  const initialCode = existingFn?.source;

  return (
    <ConnectionsRoleGate backHref="/connections/functions" surface="function">
    <ToolDeveloperPage
      scope="tenant"
      // R2 P1-3: header strings + entityKind disambiguate this from
      // the Tools authoring flow. Same shell component, function-
      // scoped strings, function-scoped welcome message + chips.
      entityKind="function"
      headerTitle="Argus Function Developer"
      headerSubtitle="AI-assisted custom function authoring"
      backHref="/connections/functions"
      saveLabel={existingFn ? 'Source edit unavailable' : 'Save as Draft'}
      initialCode={initialCode}
      initialName={existingFn?.name}
      initialDescription={existingFn?.description}
      // Bug #642: tenant_functions.runtime is 'py' or 'ts' per the
      // b54 / bU4 CHECK constraint. The previous literal comparison
      // (`runtime === 'python'`) never matched, so every Python
      // function reopened in the Tool Developer under a TypeScript
      // label. Use the shared helper so this can't regress quietly.
      initialLanguage={runtimeToEditorLanguage(existingFn?.runtime)}
      onSave={(code, name, _description, language, definition) => {
        if (existingFn) {
          toast.info('Source editing is not wired in V1', {
            description:
              'No changes were saved. Create a new draft or use deploy/disable actions from the function list.',
          });
          return;
        }

        const trimmedName = name.trim();
        const trimmedCode = code.trim();
        if (!trimmedName || !trimmedCode) {
          toast.error('Function draft needs a name and source code before it can be saved.');
          return;
        }

        const t = toast.loading(`Saving "${trimmedName}" as a draft...`);
        createFunction.mutate(
          {
            name: trimmedName,
            description: _description.trim(),
            runtime: language === 'python' ? 'python' : 'typescript',
            source: code,
            // function_helper depth fix — persist the full definition the
            // builder produced (was dropped, leaving empty schemas / no egress).
            inputSchemaJson: definition?.inputSchemaJson,
            outputSchemaJson: definition?.outputSchemaJson,
            sideEffects: definition?.sideEffects,
            networkHosts: definition?.networkHosts,
          },
          {
            onSuccess: () => {
              toast.dismiss(t);
              toast.success(`Function "${trimmedName}" saved as draft`);
              navigate({ to: '/connections/functions' });
            },
            onError: (err) => {
              toast.dismiss(t);
              toast.error('Function draft was not saved', {
                description: err.message,
              });
            },
          },
        );
      }}
    />
    </ConnectionsRoleGate>
  );
}
